import { Fragment, useCallback, useEffect, useState } from 'react'
import { theseus2 } from '../../api/theseus'
import { useDispatch, useSelector } from 'react-redux'
import { setIsLaunch } from '../../modules/miscUi/actions'
import { IPool } from '../PoolDetails/Missions/types'
import InvestmentInfo, {
  CoreInvestmentInfo,
} from './Allocations/InvestmentInfo'
import { Box } from '../../shared_components/UI/Box'
import styled, { css } from 'styled-components'
import { amountFromWei } from '../../utils/amount'
import { USDB_DECIMALS } from '../../config'
import { useGetContractByNameQuery } from '../../modules/web3/api'
import { toast } from 'react-toastify'
import useWeb3 from '../../hooks/useWeb3'
import { selectAccount } from '../../modules/web3/selectors'
import {
  AllocationCard,
  Button,
  ChainIcon,
  Logo,
  LogoAndTitle,
  TokenName,
  TopBar,
} from './Allocations/styles'
import { Container as InvestmentInfoContainer } from './Allocations/InvestmentInfo/styles'
import Big from 'big.js'
import { ThemedButton } from '../../shared_components/ThemedButton'

import AnimatedLoader from '../../shared_components/AnimatedLoader'
import { formatDateString } from '../PoolDetails/Details'
import { obfuscateEthWalletAddress } from '../../shared_components/SocialConnectButtons/Twitter'

import NoAllocations from './Allocations/NoAllocations'
import useNetworkId from '../../hooks/useNetworkId'
import { ethers } from 'ethers'
import deepCopy from '../../utils/deepCopy'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'

export type Stakes = {
  amount: string
  share: string
  claimed: string
  left: string
  vestingPoint: string
  amountFinal: string
}

type VestingHistory = {
  user: string
  tx_hash: string
  tx_amount: string
  block_number: string
  date: string
}

export type PoolWithInvestments = {
  pool: IPool
  investment: {
    totalInWei: string
    txHashes: string[]
  }
  coreInvestmentInfo: CoreInvestmentInfo
  vestingReady: boolean
  vestingHistory: VestingHistory[]
  hasPoolFailed: boolean
  poolFailedRefundHistory: VestingHistory[]
}

const Launches = () => {
  const [poolsWithInvestments, setPoolsWithInvestments] = useState<
    PoolWithInvestments[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useDispatch()
  const account = useSelector(selectAccount)

  const { provider } = useWeb3()
  const { networkId } = useNetworkId()

  const { data: tokenSaleUsdbContractData, error: tokenSaleUsdbContractError } =
    useGetContractByNameQuery({ name: 'TokenSaleUSDB', chainId: networkId })

  const { data: tokenSaleEthContractData, error: tokenSaleEthContractError } =
    useGetContractByNameQuery({ name: 'TokenSaleETH', chainId: networkId })

  useEffect(() => {
    if (!tokenSaleUsdbContractError) return
  }, [tokenSaleUsdbContractError])

  useEffect(() => {
    if (!tokenSaleEthContractError) return
  }, [tokenSaleEthContractError])

  const fetchVestingInfoForUser = useCallback(async () => {
    try {
      setIsLoading(true)

      const { data } = await theseus2('/vesting/launches/all')

      const {
        contract: { abi: tokenSaleUsdbAbi },
      } = tokenSaleUsdbContractData
      const {
        contract: { abi: tokenSaleEthAbi },
      } = tokenSaleEthContractData

      if (!tokenSaleEthAbi) throw new Error('No abi')
      if (!tokenSaleUsdbAbi) throw new Error('No abi')
      if (!data?.poolsWithInvestments) throw new Error('No data')
      const poolsAndInvestmentsToFormat = data.poolsWithInvestments
      const formattedPoolsWithInvestments = [] as PoolWithInvestments[]
      for (const poolAndInvestment of poolsAndInvestmentsToFormat) {
        const {
          pool,
          investment,
          vestingReady,
          hasClaimedRefund,
          vestingHistory,
          poolFailedRefundHistory,
        } = poolAndInvestment

        if (!pool || !investment) throw new Error('No pool or investment')

        const { poolAddress, privateSale, poolSettings, hasFailed } = pool

        const hasPoolFailed = hasFailed

        if (!poolAddress) throw new Error('No pool address')
        if (!privateSale) throw new Error('No private sale')
        if (!poolSettings) throw new Error('No pool settings')

        const { totalRaiseLimit, raiseCurrency, vestingSettings } = poolSettings
        const { vestingPeriodStart } = vestingSettings
        const isEthRaise = raiseCurrency === 'eth'
        const tokenSaleAbi = isEthRaise ? tokenSaleEthAbi : tokenSaleUsdbAbi

        const { price } = privateSale
        const pricePerToken = Number(price)

        const tokenSaleContractInstace = createContract(
          tokenSaleAbi.slice() as ethers.InterfaceAbi,
          poolAddress,
          provider,
          account as string,
        )

        const poolState = await tokenSaleContractInstace.state()
        const userStakes = await tokenSaleContractInstace.stakes(account)

        const totalSoldForPoolInUsdb = amountFromWei(
          poolState[0],
          USDB_DECIMALS,
        ).toNumber()

        const isDiluted = totalSoldForPoolInUsdb > totalRaiseLimit

        const userBought = userStakes[0]

        const ratio = isDiluted ? totalRaiseLimit / totalSoldForPoolInUsdb : 1

        let refund, tokensReceived, investmentPerToken, investmentAfterDilution

        const userInvestment = amountFromWei(
          userBought,
          USDB_DECIMALS,
        ).toNumber()

        tokensReceived = userInvestment * (1 / pricePerToken)

        if (isDiluted) {
          tokensReceived *= ratio
          investmentPerToken = tokensReceived / userInvestment
          investmentAfterDilution = userInvestment * ratio
          refund = userInvestment - investmentAfterDilution
        }

        const coreInvestmentInfo = {
          userInvestment,
          tokensReceived,
          refund,
          investmentPerToken,
          pricePerToken,
          investmentAfterDilution,
          pool,
          hasClaimedRefund,
          isEthRaise,
          vestingBegins: new Date(vestingPeriodStart),
          hasPoolFailed,
          hasPoolFailedRefund: !!poolFailedRefundHistory.length,
        }
        formattedPoolsWithInvestments.push({
          pool,
          investment,
          coreInvestmentInfo,
          vestingReady,
          vestingHistory,
          hasPoolFailed,
          poolFailedRefundHistory,
        })
      }
      setPoolsWithInvestments(formattedPoolsWithInvestments)
    } catch (error) {
      console.error(error)
      const theseusError = error.response?.data?.message
      toast.error(theseusError || 'Error fetching vesting')

      setError(theseusError || 'Error fetching vesting')
    } finally {
      setIsLoading(false)
      setHasFetched(true)
    }
  }, [tokenSaleUsdbContractData, provider, account, tokenSaleEthContractData])

  useEffect(() => {
    dispatch(setIsLaunch(true))
    return () => {
      dispatch(setIsLaunch(false))
    }
  }, [dispatch])

  useEffect(() => {
    if (!tokenSaleUsdbContractData?.contract?.abi) return
    if (!tokenSaleEthContractData?.contract?.abi) return
    if (!provider) return
    if (!account) return

    fetchVestingInfoForUser()
  }, [
    fetchVestingInfoForUser,
    tokenSaleUsdbContractData,
    provider,
    account,
    tokenSaleEthContractData,
  ])

  const [openTabs, setOpenTabs] = useState<number[]>(
    poolsWithInvestments.slice(0, 3).map((_, i) => i),
  )

  useEffect(() => {
    setOpenTabs([...poolsWithInvestments.slice(0, 3).map((_, i) => i)])
  }, [poolsWithInvestments])

  return (
    <LaunchesContainer dc>
      {hasFetched && !poolsWithInvestments.length && !isLoading && (
        <NoAllocations />
      )}
      {isLoading ? (
        <LoadingContainer c>
          <AnimatedLoader onlyWhite size={64} />
        </LoadingContainer>
      ) : (
        poolsWithInvestments
          .slice()
          .sort(
            (a, b) =>
              new Date(a.pool.privateSale.end).getTime() -
              new Date(b.pool.privateSale.end).getTime(),
          )
          .map(
            (
              {
                pool,
                investment,
                coreInvestmentInfo,
                vestingReady,
                vestingHistory,
                poolFailedRefundHistory,
              },
              i,
            ) => {
              const isOpen = openTabs.includes(i)
              const { refund } = coreInvestmentInfo
              const { poolAddress, name, about, privateSale } = pool
              const {
                logo: { url: logoUrl },
              } = about
              const { end } = privateSale
              const poolNotEnded =
                new Date(end).getTime() > new Date().getTime()

              if (!poolAddress) return <></>
              return (
                <AllocationCard dc key={poolAddress} isOpen={isOpen}>
                  <TopBar>
                    <LogoAndTitle align="center">
                      <Logo src={logoUrl} />
                      <Box ac g={4}>
                        <TokenName>{name}</TokenName>
                      </Box>
                    </LogoAndTitle>
                    <Box flex={0} align="center">
                      <ChainIcon src={`/img/icons/chains/${'blast'}.svg`} />
                      <Button
                        isOpen={isOpen}
                        onClick={() => {
                          setOpenTabs((p) =>
                            isOpen
                              ? [...p].filter((id) => id !== i)
                              : [...p, i],
                          )
                        }}
                      >
                        <img
                          style={{ transform: 'rotate(180deg)' }}
                          src="/img/icons/allocation-arrow-down.svg"
                          alt="down arrow"
                        />
                      </Button>
                    </Box>
                  </TopBar>
                  <Box dc g={0}>
                    <InvestmentInfo
                      poolNotEnded={poolNotEnded}
                      vestingReady={vestingReady}
                      hasRefund={!!refund}
                      coreInvestmentInfo={coreInvestmentInfo}
                    />
                    {!poolNotEnded && vestingReady && (
                      <VestingProgress
                        pool={pool}
                        tokenSaleAbi={
                          tokenSaleUsdbContractData?.contract?.abi ?? []
                        }
                        vestingHistory={vestingHistory}
                      />
                    )}
                    {!poolNotEnded && !!poolFailedRefundHistory?.length && (
                      <RefundHistory
                        poolFailedRefundHistory={poolFailedRefundHistory}
                        isEthRaise={coreInvestmentInfo.isEthRaise}
                      />
                    )}
                  </Box>
                </AllocationCard>
              )
            },
          )
      )}
    </LaunchesContainer>
  )
}

export default Launches

const LaunchesContainer = styled(Box)`
  z-index: 10;
  margin-top: 3rem;
  gap: 2rem;
`

export function createContract(
  abi: ethers.InterfaceAbi,
  address: string,
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
  from?: string,
) {
  const contract = new ethers.Contract(
    address,
    JSON.parse(JSON.stringify(abi)), // fixes object not extensible
    provider,
  )
  // injectLoggingIntoContract({
  //   by: 'provider',
  //   contractInstance: contract,
  //   providerChainId:
  //     'networkVersion' in (provider?.currentProvider as any)
  //       ? (provider?.currentProvider as any)?.networkVersion
  //       : undefined,
  //   address,
  //   from,
  // })
  return contract
}

// function injectLoggingIntoContract({
//   by,
//   contractInstance,
//   address,
//   from,
//   providerChainId,
// }: {
//   by: 'provider' // ethers doesn't really cooperate with this logging injection - the methods are all non-configurable so proxy doesn't work and even object.defineproperty
//   contractInstance: Web3Contract
//   address: string
//   from: string
//   providerChainId?: string
// }) {
//   const handler = {
//     get(target, propKey) {
//       const originalMethod = target[propKey]
//
//       // Only intercept if it's a function (contract method)
//       if (typeof originalMethod === 'function') {
//         return function (...args) {
//           console.log(`${by} calling method: ${propKey} with args:`, args, {
//             address,
//             from,
//             providerChainId,
//           })
//
//           try {
//             const result = originalMethod(...args) // Call the method with the provided arguments
//
//             // If it's a promise (like a ), handle logging asynchronously
//             if (result && typeof result.then === 'function') {
//               return result
//                 .then((res) => {
//                   console.log(`${by} result from method: ${propKey}`, res, {
//                     address,
//                     from,
//                     providerChainId,
//                   })
//                   return res
//                 })
//                 .catch((err) => {
//                   console.error(`${by} error calling method: ${propKey}`, err, {
//                     address,
//                     from,
//                     providerChainId,
//                   })
//                   throw err
//                 })
//             }
//
//             // Return the result directly if it's not a promise
//             return result
//           } catch (err) {
//             console.error(`${by} error calling method: ${propKey}`, err, {
//               address,
//               from,
//               providerChainId,
//             })
//             throw err
//           }
//         }
//       }
//
//       // If not a function, just return the original property (non-methods like 'methods' itself)
//       return originalMethod
//     },
//   }
//
//   // Replace contractInstance.methods with a Proxy to intercept all method calls
//   contractInstance.methods = new Proxy(contractInstance.methods, handler)
// }

export async function createEthersContractWithSigner(
  contractAddress: string,
  contractAbi: ethers.InterfaceAbi,
  accountAddress: string,
  walletProvider: ethers.Eip1193Provider,
) {
  try {
    const ethersProvider = new ethers.BrowserProvider(walletProvider)
    const factoryContract = new ethers.Contract(
      contractAddress,
      deepCopy(contractAbi),
      ethersProvider,
    )
    const signer = await ethersProvider.getSigner(accountAddress)
    const contractWithSigner = factoryContract.connect(signer)
    return contractWithSigner
  } catch (error) {
    console.error('error making contract instance', error)
    return null
  }
}

const ProgressContainer = styled(InvestmentInfoContainer)`
  padding: 24px;
  margin: 0;
  span.title {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    opacity: 0.6;
  }
`

const oneDayInSeconds = 60 * 60 * 24
const oneWeekInSeconds = oneDayInSeconds * 7
const oneMonthInSeconds = oneDayInSeconds * 28

export const VESTING_PERIODS = [
  { label: 'Instant Vesting', value: '1' },
  { label: 'Daily Vesting', value: String(oneDayInSeconds) },
  {
    label: 'Weekly Vesting',
    value: String(oneWeekInSeconds),
  },
  { label: 'Monthly Vesting', value: String(oneMonthInSeconds) },
]

function VestingProgress({
  pool,
  tokenSaleAbi,
  vestingHistory,
}: {
  pool: IPool
  tokenSaleAbi: ethers.InterfaceAbi
  vestingHistory: VestingHistory[]
}) {
  const [tokensClaimed, setTokensClaimed] = useState(0)
  const [tokensReadyToClaim, setTokensReadyToClaim] = useState(0)
  const [totalTokensAvailable, setTotalTokensAvailable] = useState(0)
  const [stakes, setStakes] = useState<Stakes | null>(null)
  const [isClaiming, setIsClaiming] = useState(false)

  const { provider } = useWeb3()
  const account = useSelector(selectAccount)

  const {
    poolSettings: { vestingSettings },
  } = pool
  const {
    poolAddress,
    privateSale: { price: tokenPrice },
  } = pool

  const {
    firstVestPercentage,
    vestingPeriodStart,
    vestingInvervalInSeconds,
    numberOfVestingPoints,
    differentInitialVestingValue,
  } = vestingSettings

  const vestingPeriodName =
    VESTING_PERIODS.find((period) => period.value === vestingInvervalInSeconds)
      ?.label ?? 'Monthly'
  const [currentVestingPoint, setCurrentVestingPoint] = useState(0)

  const fetchAndSetStakes = useCallback(async () => {
    if (!provider) return
    if (!pool) return
    if (!poolAddress) return
    if (!account) return

    const { decimals: decimalsRaw } = pool
    const decimals = Number(decimalsRaw)

    const tokenSaleContractInstace = createContract(
      tokenSaleAbi,
      poolAddress,
      provider,
      account as string,
    )

    const stakes = await tokenSaleContractInstace.stakes(account)
    const canClaim = await tokenSaleContractInstace.canClaim(account)

    const alreadyClaimedInWei = stakes[2]

    const amountThatCanBeClaimedInUsdbAfterDilution = amountFromWei(
      new Big(canClaim[0]),
      decimals,
    ).toNumber()

    const isInstantVesting = numberOfVestingPoints === 1

    const totalTokensAvailableAfterDilution =
      amountThatCanBeClaimedInUsdbAfterDilution * (1 / Number(tokenPrice))

    const isPastFirstVestingPoint =
      new Date().getTime() > new Date(vestingPeriodStart).getTime()

    let currentVestPercentage = firstVestPercentage

    const subsequentVestingPointPercentage = !differentInitialVestingValue
      ? firstVestPercentage
      : (100 - firstVestPercentage) / (numberOfVestingPoints - 1)

    if (isPastFirstVestingPoint) {
      const secondsSinceFirstVestingPoint =
        (new Date().getTime() - new Date(vestingPeriodStart).getTime()) / 1000

      const secondsSinceFirstVestingPointRounded = Math.floor(
        secondsSinceFirstVestingPoint,
      )

      const amountOfPointsElapsedSinceFirstVestingPoint = Math.min(
        Math.floor(
          secondsSinceFirstVestingPointRounded /
            Number(vestingInvervalInSeconds),
        ),
        numberOfVestingPoints - 1,
      )

      const currVestingPoint = stakes['vestingPoint'] ?? 0
      setCurrentVestingPoint(currVestingPoint)

      currentVestPercentage =
        firstVestPercentage +
        amountOfPointsElapsedSinceFirstVestingPoint *
          subsequentVestingPointPercentage
    }

    const alreadyClaimedInEth = amountFromWei(
      new Big(alreadyClaimedInWei),
      decimals,
    ).toNumber()

    const totalTokensAvailableNow = Math.max(
      isInstantVesting
        ? totalTokensAvailableAfterDilution - alreadyClaimedInEth
        : isPastFirstVestingPoint
        ? totalTokensAvailableAfterDilution * (currentVestPercentage / 100) -
          alreadyClaimedInEth
        : 0,
      0,
    )

    setTokensReadyToClaim(totalTokensAvailableNow)
    setTotalTokensAvailable(totalTokensAvailableAfterDilution)
    setTokensClaimed(alreadyClaimedInEth)
    setStakes(stakes)
  }, [
    pool,
    provider,
    tokenSaleAbi,
    account,
    firstVestPercentage,
    tokenPrice,
    vestingPeriodStart,
    differentInitialVestingValue,
    numberOfVestingPoints,
    poolAddress,
    vestingInvervalInSeconds,
  ])

  useEffect(() => {
    if (!provider || !tokenSaleAbi || !account) return
    fetchAndSetStakes()
  }, [fetchAndSetStakes, pool, provider, tokenSaleAbi, account])

  const { primaryWallet } = useDynamicContext()
  async function claimTokens() {
    try {
      setIsClaiming(true)
      if (!provider) return
      if (!poolAddress) return

      const tokenSaleContractInstace = createContract(
        tokenSaleAbi,
        poolAddress,
        provider,
        account as string,
      )
      const gas = await tokenSaleContractInstace.vesting.estimateGas()
      await tokenSaleContractInstace.vesting({ gasLimit: gas })
      const tokenAddress = pool?.poolSettings?.tokenAddress
      const symbol = pool?.ticker
      const decimals = pool?.decimals
      if (!tokenAddress) throw new Error('No token address')
      if (!symbol) throw new Error('No symbol')
      if (!decimals) throw new Error('No decimals')

      const isValid =
        primaryWallet?.key === 'metamask' &&
        !primaryWallet?.key?.includes('okx')
      if (!!window?.ethereum && isValid) {
        await (window as any)?.ethereum?.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: tokenAddress,
              symbol,
              decimals,
            },
          },
        })
      }
      await fetchAndSetStakes()
      toast.success('Claim successful')
    } catch (error) {
      console.error(error)
      toast.error('Error claiming tokens')
    } finally {
      setIsClaiming(false)
    }
  }

  const claimedPercentage = (tokensClaimed / totalTokensAvailable) * 100
  const readyPercentage = (tokensReadyToClaim / totalTokensAvailable) * 100

  const metrics = [
    {
      title: 'Claimed',
      amount: tokensClaimed,
      displayBar: true,
      widthPercentage: claimedPercentage,
      opacity: 0.5,
    },
    {
      title: 'Claimable',
      amount: tokensReadyToClaim,
      displayBar: true,
      widthPercentage: readyPercentage,
    },
    {
      title: 'Locked',
      amount: totalTokensAvailable - tokensClaimed - tokensReadyToClaim,
      displayBar: true,
      widthPercentage: 100 - claimedPercentage - readyPercentage,
      saturation: 0,
    },
  ]

  const nothingToClaim = tokensReadyToClaim < 1

  return (
    <>
      <ProgressContainer>
        <Box dc g={32}>
          <span className="title">OVERALL PROGRESS</span>
          <Box g={32} dc>
            <ProgressBarContainer>
              {metrics.map((metric, i) => {
                const { displayBar, widthPercentage, opacity, saturation } =
                  metric
                return !displayBar || !widthPercentage ? (
                  <></>
                ) : (
                  <Bar
                    key={`bar-${i}`}
                    widthPercentage={widthPercentage}
                    opacity={opacity}
                    saturation={saturation}
                  />
                )
              })}
            </ProgressBarContainer>
            <Metrics>
              <Box g={16} columnMobile>
                <Box className="metric-inner" ac g={8} fu aw>
                  <div className="tiny-bar">
                    <ProgressBarContainer>
                      {metrics.map((metric, i) => {
                        const {
                          displayBar,
                          widthPercentage,
                          opacity,
                          saturation,
                        } = metric
                        return !displayBar || !widthPercentage ? (
                          <Fragment key={`frag-${i}`}></Fragment>
                        ) : (
                          <Bar
                            key={`bar-${i}`}
                            widthPercentage={widthPercentage}
                            opacity={opacity}
                            saturation={saturation}
                          />
                        )
                      })}
                    </ProgressBarContainer>
                  </div>
                  <span>Total {totalTokensAvailable.toFixed(0)}</span>
                </Box>
                {metrics.map((metric, i) => {
                  const { title, amount, opacity, saturation } = metric
                  if (amount === 0) return <></>
                  return (
                    <Box className="metric-inner" key={i} ac g={8} fu aw>
                      <div
                        className="dot"
                        style={{
                          opacity,
                          filter: `saturate(${saturation})`,
                        }}
                      />
                      <span>
                        {title} {amount.toFixed(0)}
                      </span>
                    </Box>
                  )
                })}
              </Box>
              <Box dc afe jfe className="metric-inner">
                <span>
                  {currentVestingPoint} / {numberOfVestingPoints}
                </span>
                <span>{vestingPeriodName}</span>
              </Box>
            </Metrics>
            <ThemedButton
              $styleType="primary"
              onClick={claimTokens}
              disabled={nothingToClaim || isClaiming}
            >
              {isClaiming ? (
                <AnimatedLoader size={12} />
              ) : nothingToClaim ? (
                'Nothing to claim'
              ) : (
                'Claim Tokens'
              )}
            </ThemedButton>
          </Box>
        </Box>
      </ProgressContainer>
      {!!vestingHistory.length && (
        <ClaimHistory
          vestingHistory={vestingHistory}
          totalTokensAvailable={totalTokensAvailable}
        />
      )}
    </>
  )
}

function RefundHistory({
  poolFailedRefundHistory,
  isEthRaise,
}: {
  poolFailedRefundHistory: VestingHistory[]
  isEthRaise?: boolean
}) {
  return (
    <ClaimHistoryOuter>
      <h5>Refund History</h5>
      <ClaimHistoryInner>
        <table>
          <thead>
            <tr>
              <th>TX Hash</th>
              <th>Amount</th>
              <th>Date of Claim</th>
            </tr>
          </thead>
          <tbody>
            {poolFailedRefundHistory.map((history, i) => {
              const { tx_hash, tx_amount, date } = history
              const isLast = i === poolFailedRefundHistory.length - 1

              return (
                <tr key={i} className={isLast ? 'last' : ''}>
                  <td>
                    <a
                      href={
                        process.env.REACT_APP_BLOCK_EXPLORER + `/tx/${tx_hash}`
                      }
                      target="_blank"
                      rel="noopenner noreferrer"
                    >
                      {obfuscateEthWalletAddress(tx_hash)}
                    </a>
                  </td>
                  <td>
                    {isEthRaise ? '' : '$'}
                    {amountFromWei(new Big(tx_amount), 18).toString()}
                    {!isEthRaise ? '' : ' ETH'}
                  </td>
                  <td>{formatDateString(date)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ClaimHistoryInner>
    </ClaimHistoryOuter>
  )
}

function ClaimHistory({
  vestingHistory,
  totalTokensAvailable,
}: {
  vestingHistory: VestingHistory[]
  totalTokensAvailable: number
}) {
  return (
    <ClaimHistoryOuter>
      <h5>Claim History</h5>
      <ClaimHistoryInner>
        <table>
          <thead>
            <tr>
              <th>TX Hash</th>
              <th>Amount</th>
              <th>Percentage of Total</th>
              <th>Date of Claim</th>
            </tr>
          </thead>
          <tbody>
            {vestingHistory.map((history, i) => {
              const { tx_hash, tx_amount, date } = history
              const percentageOfTotal =
                (Number(tx_amount) / totalTokensAvailable) * 100
              const isLast = i === vestingHistory.length - 1
              return (
                <tr key={i} className={isLast ? 'last' : ''}>
                  <td>
                    <a
                      href={
                        process.env.REACT_APP_BLOCK_EXPLORER + `/tx/${tx_hash}`
                      }
                      target="_blank"
                      rel="noopenner noreferrer"
                    >
                      {obfuscateEthWalletAddress(tx_hash)}
                    </a>
                  </td>
                  <td>{Number(tx_amount).toLocaleString()}</td>
                  <td>{Math.ceil(percentageOfTotal)}%</td>
                  <td>{formatDateString(date)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ClaimHistoryInner>
    </ClaimHistoryOuter>
  )
}

const ClaimHistoryOuter = styled(Box)`
  padding-top: 2rem;
  gap: 0.5rem;
  flex-direction: column;
  h5 {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.14px;
  }
`

const tableStyles = css`
  table {
    width: 100%;
  }
  th {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */
    text-transform: uppercase;
    padding-bottom: 1rem;
    text-align: left;
  }
  tr {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    letter-spacing: 0.48px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    height: 48px;
  }
  a {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }
  tr.last {
    border-bottom: none;
  }
`

export const ClaimHistoryInner = styled(InvestmentInfoContainer)`
  height: 100%;
  padding: 1rem 2rem;
  ${tableStyles}
`

type BarProps = {
  widthPercentage: number
  opacity: number
  saturation: number
}

const Bar = styled.div<BarProps>`
  height: 12px;
  flex: ${({ widthPercentage }) => widthPercentage / 100};
  overflow: hidden;
  opacity: ${({ opacity }) => opacity};
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  filter: saturate(${({ saturation }) => saturation});
`

const ProgressBarContainer = styled(Box)`
  gap: 0.5rem;
  transition: all 0.3s ease-in-out;
`

const Metrics = styled(Box)`
  gap: 2rem;
  span {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    opacity: 0.6;
  }
  .dot {
    width: 12px;
    height: 12px;
    background: ${({ theme }) => theme.colors.brandPrimary};
    border-radius: 4px;
  }
  .tiny-bar {
    width: 24px;
    gap: 0;
    ${Bar} {
      margin-right: -2px;
      border-radius: 4px;
    }
    ${ProgressBarContainer} {
      gap: 0;
    }
  }
`

const LoadingContainer = styled(Box)`
  min-height: 200px;
`
